import React from "react";
import {
    Box, Button, Chip, Typography,
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline, DownloadOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const breadcrumbs = [
    { title: "Leads", path: "/app/crm/vendor-lead/list" },
    { title: "Bulk Import Review", path: -1 },
    { title: "Import Summary" },
];

const VendorLeadImportSummaryScreen = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const {
        results = [],
        leads = [],
        importedCount = 0,
        totalCount = 0,
    } = state || {};

    const skippedCount = totalCount - importedCount;

    // Merge backend results with original lead rows by index for richer display
    const rows = results.map((result, idx) => {
        const lead = leads[idx] ?? {};
        const displayName =
            lead.name || lead.companyName || lead.contactPersonName || "—";
        const displayContact = lead.email || lead.phone || result.tag || "—";
        return {
            index: idx + 1,
            displayName,
            displayContact,
            status: result.status,       // "created" | "skipped"
            reason: result.reason ?? "—",
        };
    });

    // ── PDF Export ────────────────────────────────────────────────────────────
    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

        // Title
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Vendor Lead Bulk Import Summary", 40, 40);

        // Stats line
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(
            `Total: ${totalCount}   |   Imported: ${importedCount}   |   Skipped: ${skippedCount}   |   Generated: ${new Date().toLocaleString()}`,
            40,
            60,
        );

        // Table
        autoTable(doc, {
            startY: 75,
            head: [["#", "Name / Company", "Email / Phone", "Status", "Reason"]],
            body: rows.map((r) => [
                r.index,
                r.displayName,
                r.displayContact,
                r.status === "created" ? "Imported" : "Skipped",
                r.status === "created" ? "" : r.reason,
            ]),
            styles: { fontSize: 9, cellPadding: 5 },
            headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: "bold" },
            columnStyles: {
                0: { cellWidth: 30 },
                3: { cellWidth: 70 },
            },
            didParseCell(data) {
                // Colour the Status column cells
                if (data.column.index === 3 && data.section === "body") {
                    const val = data.cell.raw;
                    if (val === "Imported") {
                        data.cell.styles.textColor = [27, 128, 80];
                        data.cell.styles.fontStyle = "bold";
                    } else {
                        data.cell.styles.textColor = [198, 40, 40];
                        data.cell.styles.fontStyle = "bold";
                    }
                }
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
        });

        doc.save(`lead-import-summary-${Date.now()}.pdf`);
    };

    // ── Guard ─────────────────────────────────────────────────────────────────
    if (!results.length) {
        return (
            <BreadcrumbLayout breadcrumbs={breadcrumbs}>
                <BreadcrumbLayout.Paper>
                    <Box p={3}>
                        <Typography color="text.secondary">
                            No import results found. Please go back and run an import first.
                        </Typography>
                    </Box>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        );
    }

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>

                {/* ── Stat Cards ─────────────────────────────────────────────── */}
                <Box px={3} pt={3} pb={2} display="flex" gap={2} flexWrap="wrap">
                    <StatCard label="Total Submitted" value={totalCount} color="text.primary" />
                    <StatCard label="Successfully Imported" value={importedCount} color="success.main" />
                    <StatCard label="Skipped / Failed" value={skippedCount} color="error.main" />
                </Box>

                {/* ── Table ──────────────────────────────────────────────────── */}
                <Box px={3} pb={3}>
                    <Box
                        sx={{
                            overflowX: "auto",
                            overflowY: "auto",
                            maxHeight: 520,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                        }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                            <thead>
                                <tr>
                                    {["#", "Name / Company", "Email / Phone", "Status", "Reason"].map((h) => (
                                        <th key={h} style={thStyle(theme)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.index} style={trStyle(theme, row.index, row.status === "skipped")}>
                                        <td style={tdStyle(theme)}>{row.index}</td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2">{row.displayName}</Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.displayContact}
                                            </Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            {row.status === "created" ? (
                                                <Chip
                                                    icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                                                    label="Imported"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                                                    label="Skipped"
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                            )}
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            {row.status === "skipped" ? (
                                                <Typography variant="caption" color="error.main">
                                                    {row.reason}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                {/* ── Actions ────────────────────────────────────────────────── */}
                <Box px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadOutlined />}
                        onClick={handleDownloadPDF}
                    >
                        Download PDF
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/app/crm/vendor-lead/list")}
                    >
                        Go to Leads
                    </Button>
                </Box>

            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorLeadImportSummaryScreen;

// ── Small stat card ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, color }) => (
    <Box
        sx={{
            px: 3, py: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            minWidth: 150,
            textAlign: "center",
        }}
    >
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
);

// ── Theme-aware styles ────────────────────────────────────────────────────────
const thStyle = (theme) => ({
    padding: "10px 12px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderRight: `1px solid ${theme.palette.divider}`,
});

const trStyle = (theme, idx, isSkipped) => ({
    backgroundColor: isSkipped
        ? theme.palette.mode === "dark"
            ? "rgba(211,47,47,0.12)"
            : "rgba(211,47,47,0.05)"
        : idx % 2 === 0
            ? theme.palette.action.hover
            : theme.palette.background.paper,
});

const tdStyle = (theme) => ({
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
});